import { useState, useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'Enter Your Address',
    description: 'Type your address and answer a few quick questions about your roofing needs. Takes less than 60 seconds.',
    icon: 'ri-map-pin-line'
  },
  {
    number: '02',
    title: 'Get Instant Estimate',
    description: 'Our AI measures your roof and generates accurate pricing based on your specifications. No waiting required.',
    icon: 'ri-flashlight-line'
  },
  {
    number: '03',
    title: 'Match With Vetted Pros',
    description: 'We connect you with top-rated local contractors who have been screened for quality and fair pricing.',
    icon: 'ri-team-line'
  },
  {
    number: '04',
    title: 'Start Your Project',
    description: 'Connect directly with your matched pro. No middlemen, no markups. Keep more money in your pocket.',
    icon: 'ri-hand-coin-line'
  }
];

const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            steps.forEach((_, index) => {
              setTimeout(() => {
                setVisibleSteps((prev) => [...prev, index]);
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-6 px-4">
            How It Works
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4 leading-relaxed">
            Four simple steps to get your roof done right — and keep thousands more in your pocket
          </p>
        </div>

        <div ref={sectionRef} className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#4A90E2] via-[#2B5BA6] to-[#4A90E2] opacity-20"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative transition-all duration-700 ${
                  visibleSteps.includes(index)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-12'
                }`}
                onMouseEnter={() => setActiveStep(index)}
                onMouseLeave={() => setActiveStep(null)}
              >
                <div className={`bg-white rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border-2 h-full flex flex-col ${
                  activeStep === index 
                    ? 'border-[#4A90E2] -translate-y-3 scale-105' 
                    : 'border-transparent hover:-translate-y-2'
                }`}>
                  {/* Step Number */}
                  <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full mb-4 sm:mb-5 lg:mb-6 transition-all duration-500 flex-shrink-0 ${
                    activeStep === index
                      ? 'bg-gradient-to-br from-[#4A90E2] to-[#2B5BA6] scale-110 rotate-12'
                      : 'bg-gradient-to-br from-slate-100 to-slate-200'
                  }`}>
                    <span className={`text-xl sm:text-2xl lg:text-3xl font-bold transition-colors duration-300 ${
                      activeStep === index ? 'text-white' : 'text-slate-700'
                    }`}>
                      {step.number}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center mb-3 sm:mb-4 transition-all duration-500 flex-shrink-0 ${
                    activeStep === index ? 'scale-110' : ''
                  }`}>
                    <i className={`${step.icon} text-2xl sm:text-3xl lg:text-4xl transition-colors duration-300 ${
                      activeStep === index ? 'text-[#4A90E2]' : 'text-slate-600'
                    }`}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-grow flex flex-col">
                    <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold mb-2 sm:mb-3 lg:mb-4 transition-colors duration-300 leading-tight ${
                      activeStep === index ? 'text-[#2B5BA6]' : 'text-gray-900'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow Indicator */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center">
                      <i className={`ri-arrow-right-line text-2xl transition-all duration-500 ${
                        activeStep === index ? 'text-[#4A90E2] scale-125' : 'text-gray-300'
                      }`}></i>
                    </div>
                  )}
                </div>

                {/* Progress Indicator */}
                <div className={`mt-3 sm:mt-4 h-1 rounded-full transition-all duration-1000 ${
                  visibleSteps.includes(index)
                    ? 'bg-gradient-to-r from-[#4A90E2] to-[#2B5BA6] w-full'
                    : 'bg-gray-200 w-0'
                }`}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
